import { Web } from 'sip.js';
import { createAudioElement } from '@/helpers/utils';
import { INCOMING_RINGER, OUTGOING_RINGER, UNAVAILABLE_PROMPT, useDialerStore } from '@/stores/dialer.store.js';
import { useUtilstStore } from '@/stores';
import logger from '@/helpers/logger';
import emitter from '@/plugins/event-bus';
import { track } from '@/helpers/analytics.js';
import { airtableInboundCallService } from './airtable';

export const initSIPPhone = async (sipCredentials, phoneNumber, numberId) => {
	/*
	 * https://github.com/onsip/SIP.js/blob/master/docs/api.md#handling-changes-in-network-state
	 * The simple user instance we use already handles reconnections
	 * */
	const dialerStore = useDialerStore();
	const utilsStore = useUtilstStore();
	const currentTime = new Date();
	const dateOptions = { day: '2-digit', month: '2-digit', year: '2-digit' };
	const hour = currentTime.getHours();
	const minute = currentTime.getMinutes();
	const second = currentTime.getSeconds();
	const hangUpSequence = () => {
		const direction = dialerStore.callStatus.outgoingCallTo ? 'outgoing' : 'incoming';
		const trackProp = {
			numberId,
			direction
		};

		if (direction === 'incoming') {
			trackProp['from'] = dialerStore.callStatus.incomingCallFrom;
		} else {
			trackProp['to'] = dialerStore.callStatus.outgoingCallTo;
		}

		track('call ended', trackProp);
		track('Call Box OFF');
		// console.log(utilsStore.isCallFromTest);
		if (utilsStore.isCallFromTest) {
			// console.log('Got Here');
			utilsStore.isCallFromTest = false;
			utilsStore.showTestCall = false;
			dialerStore.showCallFeedbackModal = true;
		}
		// console.log(utilsStore.isCallFromTest);

		dialerStore.callStatus.isOutgoingRinging = false;
		dialerStore.callStatus.isIncomingRinging = false;
		dialerStore.callStatus.numberId = null;
		dialerStore.callStatus.isCallActive = false;
		dialerStore.callStatus.hasAnswered = false;
		dialerStore.callStatus.incomingCallFrom = null;
		dialerStore.endTimer();
		dialerStore.callStatus.outgoingCallTo = null;
		OUTGOING_RINGER.stop();
		INCOMING_RINGER.stop();
		if (dialerStore.callStatus._incomingCallRingDurationInterval) {
			clearInterval(dialerStore.callStatus._incomingCallRingDurationInterval);
		}
		dialerStore.callStatus.callTransferStatus = '';
		emitter.emit('saveNote');
	};
	const delegate = {
		onCallReceived: async () => {
			dialerStore.callStatus.numberId = numberId;
			dialerStore.callStatus.isIncomingRinging = true;
			dialerStore.callStatus.hasAnswered = false;
			dialerStore.callStatus.isCallActive = true;
			dialerStore.callStatus.outgoingCallTo = null;
			if (dialerStore.callStatus.duration === 0) {
				INCOMING_RINGER.play();
				if (dialerStore.callStatus._incomingCallRingDurationInterval) {
					clearInterval(dialerStore.callStatus._incomingCallRingDurationInterval);
				}
				dialerStore.callStatus._incomingCallRingDurationInterval = setInterval(() => {
					dialerStore.callStatus.incomingCallRingDuration + 1;
				}, 1000);
			}
			track('Call Received - Dialer Showing');
		},
		onCallAnswered: async () => {
			dialerStore.callStatus.isIncomingRinging = false;
			dialerStore.callStatus.isCallActive = true;
			dialerStore.startTimer();
			INCOMING_RINGER.stop();
			if (dialerStore.callStatus._incomingCallRingDurationInterval) {
				clearInterval(dialerStore.callStatus._incomingCallRingDurationInterval);
			}
			dialerStore.isCallOngoing = true;
			setTimeout(() => {
				dialerStore.isCallOngoing = false;
			}, 5000);
			await airtableInboundCallService.updateRecord('answered');
			logger.info('Call Answered');
			dialerStore.isCallOngoing = true;
			track('call picked', { numberId });
		},
		onCallCreated: async () => {
			dialerStore.callStatus.isCallActive = true;
			dialerStore.callStatus.isOnHold = false;
			dialerStore.callStatus.isMute = false;
			dialerStore.isDialerShowing = true;
			track('Call Created - Dialer Showing');
		},
		onCallHangup: async () => {
			if (dialerStore.callStatus.isIncomingRinging) {
				if (dialerStore.callStatus.incomingCallRingDuration >= 10) {
					// un-answerd
					await airtableInboundCallService.updateRecord('unanswered');
				} else {
					// rejected
					await airtableInboundCallService.updateRecord('rejected');
				}
			}
			let direction = dialerStore.callStatus.outgoingCallTo ? 'outgoing' : 'incoming';
			await dialerStore.callStatus.callStatusCode;
			if (direction === 'outgoing') {
				switch (dialerStore.callStatus.callStatusCode) {
					case 480:
						OUTGOING_RINGER.stop();
						UNAVAILABLE_PROMPT.play();
						break;
					case 500:
						OUTGOING_RINGER.stop();
						UNAVAILABLE_PROMPT.play();
						break;
					default:
						break;
				}
				if (dialerStore.callStatus.duration === 0) {
					setTimeout(() => {
						hangUpSequence();
					}, 5000);
				} else {
					hangUpSequence();
				}
			} else {
				!dialerStore.isCallOngoing ? hangUpSequence() : '';
			}
		},
		onCallHold: async (isHeld) => {
			dialerStore.callStatus.isOnHold = isHeld;
			track('Call Placed on Hold');
		},
		onRegistered: async () => {
			logger.info('sip registered');
			track('sip registered', {
				numberId,
				phoneNumber,
				startTime: `${hour}:${minute}:${second}`,
				startDate: `${currentTime.toLocaleDateString('en-GB', dateOptions)}`
			});
			dialerStore.phoneConnectionStatuses[numberId] = {
				isConnected: true,
				isConnecting: false
			};
		},
		onUnregistered: async () => {
			logger.info('sip unregistered');
			track('sip unregistered', {
				numberId,
				phoneNumber,
				endTime: `${hour}:${minute}:${second}`,
				endDate: `${currentTime.toLocaleDateString('en-GB', dateOptions)}`
			});
			dialerStore.phoneConnectionStatuses[numberId] = {
				isConnected: false,
				isConnecting: false
			};
			track('Trying to re-register after unregistration.', {
				numberId,
				phoneNumber
			});

			try {
				const simpleUserInstance = dialerStore.simpleUserInstances[numberId];
				if (simpleUserInstance) await simpleUserInstance.register();
			} catch (error) {
				logger.error(error, 'Error re-registering after unregistration.');
			}
		},
		onServerConnect: async () => {
			track('websocket connected', {
				numberId,
				phoneNumber
			});
		},
		onServerDisconnect: async (error) => {
			track('websocket disconnected', {
				error,
				numberId,
				phoneNumber
			});
			dialerStore.phoneConnectionStatuses[numberId] = {
				isConnected: false,
				isConnecting: false
			};
			track('Trying to reconnect websocket after a server disconnect.');
			try {
				const simpleUserInstance = dialerStore.simpleUserInstances[numberId];
				if (simpleUserInstance) await simpleUserInstance.connect();
			} catch (error) {
				logger.error(error, 'Error reconnecting websocket after a server disconnect.');
			}
		}
	};

	const options = {
		aor: `sip:${sipCredentials.username}@${sipCredentials.domain}`,
		delegate,
		media: {
			constraints: {
				audio: true,
				video: false
			},
			remote: {
				audio: createAudioElement('call-audio-container') // element is in the sidemenu
			}
		},
		userAgentOptions: {
			displayName: phoneNumber,
			authorizationUsername: sipCredentials.username,
			authorizationPassword: sipCredentials.password,
			// logBuiltinEnabled: false,
			// logConfiguration: false,
			logLevel: 'error'
		}
	};

	const server = `${sipCredentials.protocol}://${sipCredentials.host}:${sipCredentials.port}`;
	const simpleUser = new Web.SimpleUser(server, options);

	const onInvite = simpleUser.sessionManager.userAgent.delegate.onInvite;

	simpleUser.sessionManager.userAgent.delegate.onInvite = async (invitation) => {
		track('incoming call', {
			numberId,
			phoneNumber,
			from: invitation.incomingInviteRequest.message.from.uri.raw.user
		});
		track('Call Box ON');
		dialerStore.callStatus.incomingCallFrom = invitation.incomingInviteRequest.message.from.uri.raw.user;
		await onInvite(invitation);
	};

	await simpleUser.connect();
	await simpleUser.register();
	return simpleUser;
};
